/**
 * Minimal PNG chunk reading/writing (pure, byte-level), used to embed the v3
 * wire JSON in an `iTXt` chunk (SPEC §7.3).
 */
import { zlibSync } from 'fflate';

import {
  concatBytes,
  inflateLimited,
  latin1Decode,
  latin1Encode,
  utf8Decode,
  utf8Encode,
} from './bytes';
import { ImportError } from './errors';

export const PNG_SIGNATURE = Uint8Array.of(137, 80, 78, 71, 13, 10, 26, 10);

export interface PngChunk {
  type: string;
  data: Uint8Array;
  /** Whether the stored CRC matched; chunks with a bad CRC are ignored by readers. */
  crcOk: boolean;
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32(bytes: Uint8Array, crc = 0xffffffff): number {
  let c = crc;
  for (const byte of bytes) c = (CRC_TABLE[(c ^ byte) & 0xff] ?? 0) ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

export function isPng(bytes: Uint8Array): boolean {
  return bytes.length >= 8 && PNG_SIGNATURE.every((byte, i) => bytes[i] === byte);
}

/** Splits a PNG into chunks. Throws `corrupt` if the structure is broken. */
export function readChunks(bytes: Uint8Array): PngChunk[] {
  if (!isPng(bytes)) throw new ImportError('corrupt', 'Not a PNG');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const chunks: PngChunk[] = [];
  let offset = 8;
  while (offset < bytes.length) {
    if (offset + 12 > bytes.length) throw new ImportError('corrupt', 'Truncated PNG chunk');
    const length = view.getUint32(offset);
    const end = offset + 12 + length;
    if (end > bytes.length) throw new ImportError('corrupt', 'Truncated PNG chunk');
    const typeBytes = bytes.subarray(offset + 4, offset + 8);
    const data = bytes.subarray(offset + 8, offset + 8 + length);
    const crcOk =
      crc32(bytes.subarray(offset + 4, offset + 8 + length)) === view.getUint32(end - 4);
    const type = latin1Decode(typeBytes);
    chunks.push({ type, data, crcOk });
    offset = end;
    if (type === 'IEND') break;
  }
  if (chunks[0]?.type !== 'IHDR' || chunks.at(-1)?.type !== 'IEND') {
    throw new ImportError('corrupt', 'PNG must start with IHDR and end with IEND');
  }
  return chunks;
}

export function encodeChunk(type: string, data: Uint8Array): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(12 + data.length);
  const view = new DataView(out.buffer);
  view.setUint32(0, data.length);
  out.set(latin1Encode(type), 4);
  out.set(data, 8);
  view.setUint32(8 + data.length, crc32(out.subarray(4, 8 + data.length)));
  return out;
}

export function writeChunks(
  chunks: readonly Pick<PngChunk, 'type' | 'data'>[],
): Uint8Array<ArrayBuffer> {
  return concatBytes([
    PNG_SIGNATURE,
    ...chunks.map((chunk) => encodeChunk(chunk.type, chunk.data)),
  ]);
}

/** Builds `iTXt` chunk data: keyword, zlib-compressed UTF-8 text, no language tag. */
export function buildITXt(keyword: string, text: string): Uint8Array<ArrayBuffer> {
  return concatBytes([
    latin1Encode(keyword),
    Uint8Array.of(0, 1, 0, 0, 0), // NUL, compression flag, method (zlib), empty lang + NUL, empty translated keyword + NUL
    zlibSync(utf8Encode(text), { level: 9 }),
  ]);
}

/** Parses `iTXt` chunk data; `null` if malformed. Throws `too-large` for oversized text. */
export function parseITXt(data: Uint8Array): { keyword: string; text: string } | null {
  const keywordEnd = data.indexOf(0);
  if (keywordEnd < 1) return null;
  const flag = data[keywordEnd + 1];
  const method = data[keywordEnd + 2];
  const langEnd = data.indexOf(0, keywordEnd + 3);
  const translatedEnd = langEnd === -1 ? -1 : data.indexOf(0, langEnd + 1);
  if (translatedEnd === -1 || (flag !== 0 && flag !== 1) || method !== 0) return null;
  const body = data.subarray(translatedEnd + 1);
  try {
    const text = utf8Decode(flag === 1 ? inflateLimited(body, 'zlib') : body);
    return { keyword: latin1Decode(data.subarray(0, keywordEnd)), text };
  } catch (error) {
    if (error instanceof ImportError && error.code === 'too-large') throw error;
    return null;
  }
}

/** The text of the first valid (CRC-checked) `iTXt` chunk with `keyword`, if any. */
export function readITXt(chunks: readonly PngChunk[], keyword: string): string | null {
  for (const chunk of chunks) {
    if (chunk.type !== 'iTXt' || !chunk.crcOk) continue;
    const parsed = parseITXt(chunk.data);
    if (parsed?.keyword === keyword) return parsed.text;
  }
  return null;
}

/** Returns a copy of `png` with an `iTXt` chunk (replacing any with the same keyword) after IHDR. */
export function insertITXt(
  png: Uint8Array,
  keyword: string,
  text: string,
): Uint8Array<ArrayBuffer> {
  const chunks = readChunks(png).filter(
    (chunk) => !(chunk.type === 'iTXt' && parseITXt(chunk.data)?.keyword === keyword),
  );
  return writeChunks([
    ...chunks.slice(0, 1),
    { type: 'iTXt', data: buildITXt(keyword, text) },
    ...chunks.slice(1),
  ]);
}
